// Iter236/237 · Vertical Waste Treatment (DG=TRATAMIENTO) dentro de Tender
// Intelligence. 100% datos ya detectados automáticamente (clasificación
// CPV+regex de `services/dg_autoclassify.py` para el DG y por keywords para
// el sub-tipo de instalación) — sin listado manual de plantas.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2, Recycle, Trophy, Timer, ExternalLink, MapPin, TrendingUp, Link2 } from "lucide-react";
import { ProvinceHeatMap, fmtEurCompact } from "./ProvinceHeatMap";

const fmtEur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const SUBTYPE_ORDER = ["VERTEDERO", "INCINERACION_VALORIZACION", "COMPOSTAJE", "CLASIFICACION", "OTRO"];

const Kpi = ({ label, value, testid, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={testid}>
    <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">{label}</div>
    <div className={`text-xl font-bold mt-1 font-mono ${accent || "text-slate-800"}`}>{value}</div>
  </div>
);

const daysCls = (dias) => dias < 0 ? "text-rose-700 font-bold"
  : dias <= 30 ? "text-rose-600 font-bold"
  : dias <= 90 ? "text-orange-600 font-semibold"
  : dias <= 180 ? "text-amber-600" : "text-slate-500";

export function WasteTreatmentPanel({ onFilterGraph }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subtype, setSubtype] = useState("all");

  useEffect(() => {
    let active = true;
    setLoading(true);
    const qs = subtype !== "all" ? `?subtype=${subtype}` : "";
    axios.get(`${API}/tender-intelligence/waste-treatment${qs}`)
      .then(r => { if (active) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [subtype]);

  const trendChartData = useMemo(() => (data?.win_rate_trend || []).map(t => ({
    quarter: t.quarter, pct: t.pct, n: t.n, label: t.label,
  })), [data]);

  if (loading && !data) return <div className="flex items-center justify-center py-16 text-slate-400" data-testid="ti-waste-treatment-loading"><Loader2 size={22} className="animate-spin"/></div>;
  if (!data) return <div className="text-sm text-slate-400 text-center py-10">No se pudo cargar el vertical de Tratamiento.</div>;

  const { kpis, top_competitors, renewals, plant_operator_links, plantas, subtype_counts, subtype_labels } = data;

  return (
    <div className="space-y-4" data-testid="ti-waste-treatment-panel">
      <div className="flex items-center gap-2 text-slate-700">
        <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Recycle size={16}/></div>
        <div>
          <h3 className="text-sm font-bold text-slate-800">Waste Treatment · Vertederos, Valorización, Compostaje, Plantas RSU</h3>
          <p className="text-[11px] text-slate-500">Sub-universo DG = Tratamiento, detectado automáticamente por CPV + texto del objeto del contrato.</p>
        </div>
      </div>

      {/* Iter237 · Filtro por sub-tipo de instalación */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="ti-wt-subtype-filter">
        <button onClick={() => setSubtype("all")}
          data-testid="ti-wt-subtype-all"
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition ${subtype === "all" ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
          Todos ({Object.values(subtype_counts).reduce((a, b) => a + b, 0)})
        </button>
        {SUBTYPE_ORDER.map(st => (
          <button key={st} onClick={() => setSubtype(st)}
            disabled={subtype_counts[st] === 0}
            data-testid={`ti-wt-subtype-${st}`}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border transition ${subtype === st ? "bg-[#007934] text-white border-[#007934]" : subtype_counts[st] === 0 ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {subtype_labels[st]} ({subtype_counts[st]})
          </button>
        ))}
      </div>

      <div className={`grid grid-cols-2 md:grid-cols-5 gap-3 ${loading ? "opacity-50" : ""}`}>
        <Kpi label="Licitaciones" value={kpis.n_total} testid="ti-wt-kpi-total"/>
        <Kpi label="Importe total" value={fmtEurCompact(kpis.importe_total)} testid="ti-wt-kpi-importe"/>
        <Kpi label="Win-rate" value={kpis.win_rate.label} accent={kpis.win_rate.pct == null ? "text-slate-400" : kpis.win_rate.pct >= 50 ? "text-emerald-700" : "text-rose-600"} testid="ti-wt-kpi-winrate"/>
        <Kpi label="Provincias" value={kpis.n_provincias} testid="ti-wt-kpi-provincias"/>
        <Kpi label="Pipeline abierto" value={`${kpis.pipeline_abierto_n} · ${fmtEurCompact(kpis.pipeline_abierto_importe)}`} testid="ti-wt-kpi-pipeline"/>
      </div>

      {/* Iter237 · Tendencia trimestral de win-rate */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ti-wt-winrate-trend">
        <div className="flex items-center gap-2 mb-2 text-slate-700">
          <TrendingUp size={14} className="text-indigo-600"/>
          <h4 className="text-sm font-semibold">Evolución trimestral del win-rate</h4>
        </div>
        {trendChartData.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-8">Sin licitaciones cerradas aún en este sub-universo.</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trendChartData} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
              <XAxis dataKey="quarter" tick={{ fontSize: 10 }}/>
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`}/>
              <Tooltip formatter={(val, name, props) => [props.payload.label, "Win-rate"]}/>
              <Line type="monotone" dataKey="pct" stroke="#007934" strokeWidth={2} dot={{ r: 3 }} connectNulls data-testid="ti-wt-trend-line"/>
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="ti-wt-province-map">
        <div className="flex items-center gap-2 mb-2 text-slate-700">
          <MapPin size={14} className="text-emerald-600"/>
          <h4 className="text-sm font-semibold">Mapa de Tratamiento por provincia</h4>
        </div>
        <ProvinceHeatMap dg="TRATAMIENTO" compact onSelectProvince={onFilterGraph}/>
      </div>

      {/* Iter237 · Vínculo Planta ↔ Operador rival ↔ vencimiento en una sola vista */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-wt-plant-operator">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
          <Link2 size={14} className="text-purple-600"/>
          <h4 className="text-sm font-semibold text-slate-800">Planta ↔ Operador rival ↔ Vencimiento</h4>
          <span className="text-[10px] text-slate-400 font-mono ml-auto">{plant_operator_links.length} concesiones operadas por rivales</span>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          {plant_operator_links.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">No hay concesiones de rivales identificadas con fecha de vencimiento estimada.</div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-slate-50"><tr className="text-slate-400 text-left border-b border-slate-100">
                <th className="py-1.5 px-2">Planta / Cliente</th><th className="px-2">Sub-tipo</th>
                <th className="px-2">Operador rival</th><th className="px-2">Provincia</th>
                <th className="text-right px-2">Vencimiento</th>
              </tr></thead>
              <tbody>
                {plant_operator_links.map(l => (
                  <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`ti-wt-plant-operator-${l.id}`}>
                    <td className="py-1.5 px-2 font-medium text-slate-700 truncate max-w-[220px]" title={l.objeto}>{l.cliente || "—"}</td>
                    <td className="px-2 text-slate-500">{subtype_labels[l.subtype]}</td>
                    <td className="px-2 font-semibold text-purple-700 truncate max-w-[140px]">{l.operador}</td>
                    <td className="px-2 text-slate-500">{l.provincia || "—"}</td>
                    <td className={`text-right px-2 ${daysCls(l.dias_restantes)}`}>
                      {l.dias_restantes < 0 ? `Hace ${Math.abs(l.dias_restantes)}d` : `En ${l.dias_restantes}d`}
                      <div className="text-[9px] text-slate-400 font-mono font-normal">{l.proxima_licitacion}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-wt-renewals">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
            <Timer size={14} className="text-orange-600"/>
            <h4 className="text-sm font-semibold text-slate-800">Calendario de renovación de concesiones</h4>
          </div>
          <div className="max-h-96 overflow-auto">
            {renewals.length === 0 && <div className="p-6 text-center text-xs text-slate-500">Sin renovaciones detectadas.</div>}
            {renewals.map(c => (
              <div key={c.id} className="border-b border-slate-100 px-3 py-2 hover:bg-slate-50 transition" data-testid={`ti-wt-renewal-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-800 truncate">{c.cliente || "Cliente sin datos"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.objeto || "—"}</div>
                    <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-2 mt-0.5">
                      {c.provincia && <span>{c.provincia}</span>}
                      {c.winner && <span>Operador actual: <b>{c.winner}</b></span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xs ${daysCls(c.dias_restantes)}`}>{c.dias_restantes < 0 ? `Hace ${Math.abs(c.dias_restantes)}d` : `En ${c.dias_restantes}d`}</div>
                    <div className="text-[9px] text-slate-400 font-mono">{c.proxima_licitacion}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-wt-competitors">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
            <Trophy size={14} className="text-rose-600"/>
            <h4 className="text-sm font-semibold text-slate-800">Operadores rivales en Tratamiento</h4>
          </div>
          <div className="p-2 space-y-2 max-h-96 overflow-auto">
            {top_competitors.length === 0 && <div className="text-center text-xs text-slate-500 py-6">Sin rivales detectados.</div>}
            {top_competitors.map((c, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-2" data-testid={`ti-wt-competitor-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-800 truncate"><span className="text-slate-400 mr-1 font-mono">#{i + 1}</span>{c.name}</span>
                  <span className="text-[10px] font-mono text-slate-500">{c.wins} adj.</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{fmtEur(c.importe)}</div>
                {c.clientes?.length > 0 && <div className="text-[10px] text-slate-400 mt-1 truncate">Clientes: {c.clientes.slice(0, 3).join(", ")}{c.clientes.length > 3 ? "…" : ""}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ti-wt-plantas">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 bg-slate-50">
          <Recycle size={14} className="text-emerald-600"/>
          <h4 className="text-sm font-semibold text-slate-800">Licitaciones de Tratamiento ({plantas.length})</h4>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-50"><tr className="text-slate-400 text-left border-b border-slate-100">
              <th className="py-1.5 px-2">Cliente</th><th className="px-2">Objeto</th><th className="px-2">Sub-tipo</th><th className="px-2">Provincia</th>
              <th className="px-2">Estado</th><th className="text-right px-2">Importe</th><th className="px-2">Operador</th><th></th>
            </tr></thead>
            <tbody>
              {plantas.map(p => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`ti-wt-planta-${p.id}`}>
                  <td className="py-1.5 px-2 font-medium text-slate-700 truncate max-w-[140px]">{p.cliente || "—"}</td>
                  <td className="px-2 text-slate-600 truncate max-w-[200px]">{p.objeto_contrato || "—"}</td>
                  <td className="px-2 text-slate-500">{subtype_labels[p.subtype]}</td>
                  <td className="px-2 text-slate-500">{p.provincia || "—"}</td>
                  <td className="px-2 text-slate-500">{p.estado_licitacion || "—"}</td>
                  <td className="text-right px-2 font-mono text-slate-600">{fmtEur(p.presupuesto)}</td>
                  <td className="px-2 text-slate-500 truncate max-w-[120px]">{p.winner || "—"}</td>
                  <td className="px-2"><a href={`/ofertas-comercial?tab=list&open=${p.id}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline flex items-center gap-0.5" data-testid={`ti-wt-planta-open-${p.id}`}>Ficha <ExternalLink size={9}/></a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
